import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import _ from 'underscore';
import { makeStyles, } from '@fluentui/react-components';
import { ImagePanelGridCell } from '../ads-studio-asset-grid-cell';
import { SectionTilesGrid, TilesGridLoadingCell } from '../ads-studio-tile-grid';
import { AdsStudioSpinner } from '../ads-studio-spinner';
import { GridSectionName, DEFAULT_PAGE_SIZE } from '../../model/constants';

const DEFAULT_IMAGE_WIDTH = 211;
const DEFAULT_COLUMN_PADDING = 16;

const useStyles = makeStyles({
  contentGridWrapper: {
    paddingTop: '24px',
  },
  description: {
    marginBottom: '8px',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '600',
  },
});

export const PanelImageGrid = ({
  i18n,
  dataService,
  onFailedToLoadData,
  onSelectImage,
  overlayBodyRef,
  imageWidth,
  columnPadding,
  isHideDescription,
  isMultiple = true,
  isLoadingMore,
}) => {
  const {
    assets,
    totalAssetCount,
    loadMoreData,
  } = dataService;
  const classes = useStyles();
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  const onSelectedChange = useCallback((selectedItemId) => {
    if (selectedItemIds.includes(selectedItemId)) {
      const newSelectedItems = selectedItemIds.filter(itemId => itemId !== selectedItemId);
      setSelectedItemIds(newSelectedItems);
    } else {
      setSelectedItemIds(isMultiple ? [...selectedItemIds, selectedItemId] : [selectedItemId]);
    }
    onSelectImage(selectedItemId);
  }, [isMultiple, onSelectImage, selectedItemIds]);

  const assetsWithAction = useMemo(() => _.map(assets, (image) => {
    const {
      id,
    } = image;

    return ({
      ...image,
      onAction: (event) => {
        const code = _.get(event, ['detail', 'originalEvent', 'code']);
        if (code === 'Enter' || code === 'Space') {
          onSelectedChange(id);
          event.preventDefault();
        }
      },
    });
  }), [assets, onSelectedChange]);

  const imagesGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionClassName: 'ads-studio-image-section',
    itemKey: 'id',
    items: assetsWithAction,
    totalItemCount: totalAssetCount,
    config: {
      width: imageWidth || DEFAULT_IMAGE_WIDTH,
      columnPadding: columnPadding || DEFAULT_COLUMN_PADDING,
      scrollContainer: overlayBodyRef || '.ads-studio-panel-container-drawer-overlay-body',
    },
  }), [assetsWithAction, totalAssetCount, imageWidth, columnPadding, overlayBodyRef]);

  const sectionGridData = useMemo(
    () => ([imagesGridSection]),
    [imagesGridSection]
  );

  const onRenderCell = useCallback(({ item }) => {
    const { _index, id } = item;

    return (
      <ImagePanelGridCell
        item={item}
        index={_index}
        i18n={i18n}
        isMultiple={isMultiple}
        onSelectImage={onSelectedChange}
        isSelected={selectedItemIds.includes(id)}
      />
    );
  }, [i18n, onSelectedChange, selectedItemIds, isMultiple]);

  const loadNextPage = useCallback(() => {
    if (assets && assets.length >= totalAssetCount) {
      return;
    }
    const p$res = loadMoreData({ count: DEFAULT_PAGE_SIZE });
    if (p$res) {
      p$res.catch((err) => {
        if (onFailedToLoadData) {
          onFailedToLoadData(err);
        }
      });
    }
  }, [assets, loadMoreData, onFailedToLoadData, totalAssetCount]);

  const onRenderLoadingCell = useCallback(({ items, totalItemCount }) => {
    if ((items.length < totalItemCount) && (items.length > 0)) {
      return (
        <TilesGridLoadingCell onLoading={loadNextPage} key={items.length} >
          {isLoadingMore && (
            <div className="section-loading">
              <AdsStudioSpinner useCustomSize />
            </div>
          )}
        </TilesGridLoadingCell>
      );
    }
    return null;
  }, [isLoadingMore, loadNextPage]);

  return (
    <div className={classNames('panel-grid', classes.contentGridWrapper)}>
      {!isHideDescription && <div className={classes.description}>{i18n.getString(_TL_('Suggestions from recent searches'))}</div>}
      <SectionTilesGrid
        sectionData={sectionGridData}
        onRenderCell={onRenderCell}
        onRenderLoadingCell={onRenderLoadingCell}
        loadNextPage={loadNextPage}
      />
    </div>
  );
};

PanelImageGrid.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isMultiple: PropTypes.bool,
  dataService: PropTypes.shape({}),
  // logger: PropTypes.shape({
  //   logTrace: PropTypes.func,
  // }).isRequired,
  onFailedToLoadData: PropTypes.func,
  onSelectImage: PropTypes.func.isRequired,
  overlayBodyRef: PropTypes.shape({}),
};

PanelImageGrid.defaultProps = {
  dataService: {},
  onFailedToLoadData: _.noop,
  overlayBodyRef: null,
  isMultiple: true,
};
